<template>
  <div class="pitch-top">
    <el-tooltip
      :disabled="!editable || !investor.bg_img_url"
      content="Click to change a background image (1920x1080)"
      placement="bottom"
    >
      <vb-uploader
        class="image-wrapper pitch-bg"
        :enabled="editable"
        :style="investorBgStyle"
        @upload-done="onBgUploadDone($event)"
      >
        <span v-show="editable && !investor.bg_img_url"
          >Click here or drag &amp; drop an image to upload (1920x1080)</span
        >
      </vb-uploader>
    </el-tooltip>
    <div class="pitch-top-content">
      <div class="container">
        <el-tooltip
          :disabled="!editable"
          content="Click to upload a new logo"
          placement="bottom"
        >
          <div
            class="pitch-logo"
            :class="{ 'no-image-yet': !investor.logo_url }"
          >
            <vb-uploader
              class="pitch-logo-upload"
              :enabled="editable"
              :show-icon="false"
              @upload-done="onLogoUploadDone($event)"
            >
              <a
                v-if="editable"
                href
                class="pitch-image-edit hover-icon hover-icon-logo"
                @click.prevent
              >
                <i class="ion ion-camera" />
              </a>
              <img
                v-if="investor.logo_url"
                :src="investor.logo_url"
                @error="onImageDisplayError($event)"
              />
            </vb-uploader>
          </div>
        </el-tooltip>

        <h1 v-if="!editable" class="inline-block">
          {{ investor.name }}
        </h1>
        <input
          v-if="editable"
          class="form-control overview-input pitch-title"
          placeholder="Name"
          :value="investor.name"
          @blur="updateField('name', $event.target.value)"
        />

        <p class="investor-type">
          <span v-if="!editable">{{ investor.type | investorLabel }}</span>
          <InvestorTypeDropdown
            v-if="editable"
            class="seamless overview"
            :value="investor.type"
            @change="updateField('type', $event.value)"
          />
        </p>
        <textarea
          v-if="editable"
          v-autosize
          class="intro-textarea"
          placeholder="Describe investment policy"
          :value="investor.investment_policy"
          rows="2"
          @blur="updateField('investment_policy', $event.target.value)"
        />
        <p v-if="!editable" class="investor-caption">
          {{ investor.investment_policy }}
        </p>
        <ul class="pitch-details">
          <li v-if="editable || investor.website">
            <a
              v-if="!editable"
              :href="websiteLink"
              target="_new"
              class="investor-url"
            >
              {{ investor.website }}
            </a>
            <input
              v-if="editable"
              class="form-control overview-input"
              placeholder="Paste Your Website Link"
              :value="investor.website"
              @blur="updateField('website', $event.target.value)"
            />
          </li>

          <!-- country & city -->
          <li v-if="!editable">
            {{ investor.country | countryName
            }}<span v-if="investor.country && investor.city">,</span>
            {{ investor.city | capitalize }}
          </li>
          <li v-if="editable">
            <CountryDropdown
              class="seamless overview"
              :value="investor.country"
              @change="countryUpdated"
            />
          </li>
          <li v-if="editable">
            <input
              v-show="editable"
              class="form-control overview-input"
              placeholder="Type City"
              :value="investor.city"
              @blur="updateField('city', $event.target.value)"
            />
          </li>
          <!-- end country & city -->
        </ul>
        <p>
          <InvestorApplyButton
            v-if="showApplyActions && user.isStartup === true"
            :investor="investor"
            :is-icon="false"
          />
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { countryName } from '@/services/countries'
import { normalizeLink } from '@/utils'
import CountryDropdown from '@/components/CountryDropdown'
import InvestorTypeDropdown from '@/components/InvestorTypeDropdown'
import InvestorApplyButton from '@/components/InvestorApplyButton'
import { investorLabel } from '@/services/investor-types'
import * as updater from './updater'
import autosize from '@/directives/autosize'

export default {
  components: {
    CountryDropdown,
    InvestorTypeDropdown,
    InvestorApplyButton,
  },
  filters: { countryName, investorLabel },
  directives: { autosize },
  props: {
    editable: { type: Boolean, default: false },
    showApplyActions: { type: Boolean, default: true },
  },
  computed: {
    ...mapState({
      user: (state) => state.user.data,
      investor: (state) => state.investorProfile.investor,
    }),
    websiteLink() {
      const link = this.investor.website
      return normalizeLink(link)
    },
    investorBgStyle() {
      const investorBgStyle = {
        'background-image': `url(${this.investor.bg_img_url})`,
      }
      const placeholder = {
        'background-image':
          'url(http://res.cloudinary.com/vestbee/image/upload/v1497787680/kbqyxaboeqwnmrv3smil.jpg)',
      }

      return this.investor.bg_img_url ? investorBgStyle : placeholder
    },
  },
  methods: {
    async updateField(field, value) {
      return await updater.updateField(field, value)
    },
    countryUpdated(choice) {
      updater.updateField('country', choice.value)
    },
    onBgUploadDone(event) {
      updater.updateField('bg_img_url', event.data.url)
    },
    onLogoUploadDone(event) {
      updater.updateField('logo_url', event.data.url)
    },
    onImageDisplayError(event) {
      const placholder = document.getElementsByClassName(
        'pitch-logo el-tooltip',
      )[0]

      placholder.style.backgroundColor = '#eee'
      event.target.style.display = 'none'
    },
  },
}
</script>

<style scoped lang="scss">
@import '~assets/css/colors.scss';
@import '~assets/css/vars.scss';

.investor-type {
  margin: 0 0 0 150px;
  color: $grey90;
}

.investor-caption {
  margin: 15px 0 0 0;
}

.pitch-bg {
  span {
    color: $grey160;
    position: absolute;
    top: 58%;
    left: 50%;
    margin: 0;
    transform: translate(-50%, -50%);
    background: rgba(0, 0, 0, 0.7);
    border-radius: 3px;
    padding: 10px;
  }
}
</style>
